import { useTranslation } from 'react-i18next';
import { CircleAlert, CircleCheck, Film, Loader2 } from 'lucide-react';
import { AssistantMarkdown } from './AssistantMarkdown';
import { AssistantActionCard } from './AssistantActionCard';
import type { AssistantMessage } from '@/lib/ai-assistant/types';

interface Props {
  message: AssistantMessage;
  canResolve: boolean;
  resolving: boolean;
  /** The assistant is still working: a step without a result is running. */
  inProgress?: boolean;
  onResolve: (actionId: string, approve: boolean) => void;
}

export function AssistantMessageItem({
  message, canResolve, resolving, inProgress = false, onResolve,
}: Props) {
  const { t } = useTranslation();

  if (message.role === 'user') {
    const attachments = message.attachments ?? [];
    return (
      <div className="flex flex-col items-end gap-1">
        {attachments.length > 0 && (
          <div className="flex max-w-[85%] flex-wrap justify-end gap-1.5" data-testid="assistant-message-attachments">
            {attachments.map((attachment) => (
              <a
                key={attachment.url}
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                title={attachment.name ?? undefined}
                className="relative block h-24 w-24 overflow-hidden rounded-lg border border-border bg-muted"
              >
                {attachment.kind === 'image' ? (
                  <img src={attachment.url} alt={attachment.name ?? ''} className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <>
                    <video src={attachment.url} muted preload="metadata" className="h-full w-full object-cover" />
                    <Film className="absolute bottom-1 left-1 h-4 w-4 text-white drop-shadow" />
                  </>
                )}
              </a>
            ))}
          </div>
        )}
        {message.text && (
          <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-sm bg-primary px-3 py-2 text-sm text-primary-foreground">
            {message.text}
          </div>
        )}
      </div>
    );
  }

  if (message.role === 'assistant') {
    return (
      <div className="max-w-[95%] rounded-2xl rounded-bl-sm bg-muted/60 px-3 py-2 text-foreground">
        <AssistantMarkdown text={message.text || ''} />
      </div>
    );
  }

  if (message.action) {
    return (
      <AssistantActionCard
        action={message.action}
        canResolve={canResolve}
        resolving={resolving}
        onResolve={onResolve}
      />
    );
  }

  const label = message.tool_name
    ? t(`aiAssistant.tools.${message.tool_name}`, { defaultValue: message.tool_title || message.tool_name })
    : '';
  const failed = message.tool_status === 'error';
  const running = inProgress && message.tool_status === null;
  let icon = <CircleCheck className="h-3.5 w-3.5 shrink-0 text-primary" />;
  if (failed) icon = <CircleAlert className="h-3.5 w-3.5 shrink-0 text-amber-500" />;
  else if (running) icon = <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />;
  const frames = (message.attachments ?? []).filter((attachment) => attachment.kind === 'image');
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground" data-testid="assistant-tool-step">
        {icon}
        <span className="truncate">
          {failed ? t('aiAssistant.toolFailed', { tool: label }) : label}
        </span>
      </div>
      {frames.length > 0 && (
        // What the assistant looked at (editor_capture_frames).
        <div className="flex flex-wrap gap-1.5 pl-6" data-testid="assistant-step-frames">
          {frames.map((frame) => (
            <a
              key={frame.url}
              href={frame.url}
              target="_blank"
              rel="noopener noreferrer"
              title={frame.name ?? undefined}
              className="block h-16 overflow-hidden rounded-md border border-border bg-muted"
            >
              <img src={frame.url} alt={frame.name ?? ''} className="h-full w-auto object-cover" loading="lazy" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
